import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { useLanguage } from '../../contexts/LanguageContext';
import { getConversations, deleteConversation, Conversation, generateConversationSummary, invalidateConversationsCache } from '../../services/conversations';
import { exportAllCheckIns, importCheckIns } from '../../services/checkInExport';
import { WeeklyCheckIn } from '../../components/Chat/WeeklyCheckIn';
import './WeeklyCheckInPage.css';

export function WeeklyCheckInPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeConversationId, setActiveConversationId] = useState<string | null>(
    searchParams.get('id')
  );
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const importInputRef = useRef<HTMLInputElement>(null);

  const loadConversations = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getConversations();
      setConversations(data);
    } catch (err: any) {
      console.error('Failed to load conversations:', err);
      setError(err.message || 'Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  const handleSelectConversation = (id: string) => {
    setActiveConversationId(id);
    setSearchParams({ id });
  };

  const handleNewConversation = () => {
    setActiveConversationId(null);
    setSearchParams({ new: '1' });
  };

  const handleBackToList = async (options?: { redirectToTimeline?: boolean }) => {
    if (options?.redirectToTimeline) {
      navigate('/career-timeline');
      return;
    }

    // Generate summary if not already done (handles new conversations not yet in list)
    if (activeConversationId) {
      try {
        const currentConversation = conversations.find(c => c.id === activeConversationId);
        // Generate if: conversation missing from list (new) OR loaded but no summary yet
        if (!currentConversation || (currentConversation.messageCount > 0 && !currentConversation.summary)) {
          generateConversationSummary(activeConversationId).catch(err => {
            console.warn('Failed to generate conversation summary:', err);
          });
        }
      } catch (err) {
        console.warn('Error checking conversation for summary:', err);
      }
    }

    setActiveConversationId(null);
    setSearchParams({});
    invalidateConversationsCache();
    loadConversations(); // Refresh list to show updated summary
  };

  const handleDeleteConversation = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(t('weeklyCheckin.confirmDelete'))) {
      return;
    }

    try {
      setDeletingId(id);
      await deleteConversation(id);
      invalidateConversationsCache();
      setConversations(prev => prev.filter(c => c.id !== id));
      if (activeConversationId === id) {
        handleBackToList();
      }
    } catch (err: any) {
      console.error('Failed to delete conversation:', err);
      alert(t('weeklyCheckin.deleteError'));
    } finally {
      setDeletingId(null);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await exportAllCheckIns();
    } catch (err: any) {
      console.error('Export failed:', err);
      alert(`Export failed: ${err.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const user = getAuth().currentUser;
    if (!user) {
      alert('You must be signed in to import.');
      return;
    }

    setIsImporting(true);
    try {
      const { imported } = await importCheckIns(file, user.uid);
      invalidateConversationsCache();
      await loadConversations();
      alert(`Successfully imported ${imported} conversation${imported !== 1 ? 's' : ''}.`);
    } catch (err: any) {
      console.error('Import failed:', err);
      alert(`Import failed: ${err.message}`);
    } finally {
      setIsImporting(false);
      // Reset input so the same file can be re-selected if needed
      e.target.value = '';
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Unknown date';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return t('common.today');
    if (diffDays === 1) return t('common.yesterday');
    if (diffDays < 7) return `${diffDays} ${t('common.daysAgo')}`;

    return date.toLocaleDateString();
  };

  const busy = isExporting || isImporting;

  // Show chat view if conversation is selected
  if (activeConversationId !== null || searchParams.has('new')) {
    return (
      <div className="weeklyCheckInPage">
        <div className="weeklyCheckInHeader">
          <button onClick={() => { void handleBackToList(); }} className="backButton">
            ← {t('weeklyCheckin.backToList')}
          </button>
        </div>
        <WeeklyCheckIn
          conversationId={activeConversationId}
          key={activeConversationId || 'new'}
          onEndCheckIn={handleBackToList}
        />
      </div>
    );
  }

  // Show conversation list
  return (
    <div className="weeklyCheckInPage">
      <div className="weeklyCheckInListHeader">
        <h1>{t('weeklyCheckin.title')}</h1>
        <div className="headerActions">
          <input
            type="file"
            accept=".json"
            ref={importInputRef}
            onChange={handleImportFile}
            className="hiddenFileInput"
            aria-label="Import check-ins from JSON file"
          />
          <button
            className="ghostButton"
            onClick={() => importInputRef.current?.click()}
            disabled={busy}
            title="Import check-ins from a JSON backup file"
          >
            {isImporting ? 'Importing…' : '↑ Import'}
          </button>
          <button
            className="ghostButton"
            onClick={handleExport}
            disabled={busy || conversations.length === 0}
            title="Export all check-ins to a JSON file"
          >
            {isExporting ? 'Exporting…' : '↓ Export'}
          </button>
          <button onClick={handleNewConversation} className="newConversationButton">
            + {t('weeklyCheckin.startNew')}
          </button>
        </div>
      </div>

      {loading && conversations.length === 0 ? (
        <div className="skeletonList">
          {[1, 2, 3].map((i) => (
            <div key={i} className="skeletonCard">
              <div className="skeletonLine skeletonTitle" />
              <div className="skeletonLine skeletonPreview" />
              <div className="skeletonLine skeletonMeta" />
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="weeklyCheckInError">
          <p>{error}</p>
          <button onClick={loadConversations}>{t('common.retry')}</button>
        </div>
      ) : conversations.length === 0 ? (
        <div className="weeklyCheckInEmpty">
          <div className="emptyIcon">📝</div>
          <h3>{t('weeklyCheckin.noConversations')}</h3>
          <p>{t('weeklyCheckin.noConversationsDesc')}</p>
          <button onClick={handleNewConversation} className="newConversationButton large">
            {t('weeklyCheckin.startFirst')}
          </button>
        </div>
      ) : (
        <div className="conversationList">
          {conversations.map((conversation) => (
            <div
              key={conversation.id}
              className="conversationCard"
              onClick={() => handleSelectConversation(conversation.id)}
            >
              <div className="conversationCardHeader">
                <h3 className="conversationTitle">{conversation.title}</h3>
                <div className="conversationActions">
                  <span className="conversationDate">{formatDate(conversation.updatedAt)}</span>
                  <button
                    className="deleteButton"
                    onClick={(e) => handleDeleteConversation(conversation.id, e)}
                    disabled={deletingId === conversation.id}
                    title={t('common.delete')}
                    aria-label={t('common.delete')}
                  >
                    {deletingId === conversation.id ? '...' : '🗑️'}
                  </button>
                </div>
              </div>

              {(conversation.summary || conversation.preview) && (
                <p className="conversationPreview">
                  {conversation.summary || conversation.preview}
                </p>
              )}

              <div className="conversationMeta">
                <span>{conversation.messageCount} {t('weeklyCheckin.messages')}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
